import basketConfig from 'config/basket'
import type { PortionSizes } from 'config/portionSizes'
import type Cookies from 'utils/GoustoCookies'

import { unset } from './cookieHelper2'

export type BasketRecipe = {
  numPortions: PortionSizes
  quantity: number
}

type BasketRecipeMapEntry = {
  get(field: keyof BasketRecipe): number | undefined
}

type BasketEntry = BasketRecipe | BasketRecipeMapEntry | number | undefined
type BasketQuantityMap = { toJS(): Record<string, BasketEntry> }

const isBasketRecipeMapEntry = (
  entry: BasketRecipe | BasketRecipeMapEntry,
): entry is BasketRecipeMapEntry => typeof (entry as BasketRecipeMapEntry).get === 'function'

const getBasketRecipeField = (
  entry: BasketEntry,
  field: keyof BasketRecipe,
): number | undefined => {
  if (!entry || typeof entry === 'number') {
    return undefined
  }

  if (isBasketRecipeMapEntry(entry)) {
    return entry.get(field)
  }

  return entry[field]
}

const getBasketEntryQuantity = (entry: BasketEntry): number => {
  if (typeof entry === 'number') {
    return entry
  }

  return Number(getBasketRecipeField(entry, 'quantity') || 0)
}

export const getBasketRecipeQuantity = (entry: BasketEntry): number => getBasketEntryQuantity(entry)

export const normaliseBasketRecipe = (
  entry: BasketEntry,
  fallback: PortionSizes,
): BasketRecipe => ({
  quantity: getBasketEntryQuantity(entry),
  numPortions: Number(getBasketRecipeField(entry, 'numPortions') || fallback) as PortionSizes,
})

export const getBasketRecipeNumPortions = (
  entry: BasketEntry,
  fallback: PortionSizes,
): PortionSizes => Number(getBasketRecipeField(entry, 'numPortions') || fallback) as PortionSizes

export const basketSum = (items: BasketQuantityMap): number => {
  const entries = Object.values(items.toJS())

  return entries.reduce<number>((sum, entry) => sum + getBasketEntryQuantity(entry), 0)
}

export const naiveLimitReached = (
  basket: { get(key: 'recipes'): BasketQuantityMap },
  maxRecipesNum = basketConfig.maxRecipesNum,
): boolean => basketSum(basket.get('recipes')) >= maxRecipesNum

export const limitReached = (
  basketUsage: number,
  maxRecipesNum = basketConfig.maxRecipesNum,
): boolean => basketUsage >= maxRecipesNum

export function basketResetPersistent(cookie: typeof Cookies): void {
  unset(cookie, 'goustoStateStore_basket_slotId')
  unset(cookie, 'goustoStateStore_basket_recipes')
  unset(cookie, 'goustoStateStore_basket_previewOrderId')
  unset(cookie, 'goustoStateStore_basket_postcode')
  unset(cookie, 'goustoStateStore_basket_numPortions')
  unset(cookie, 'goustoStateStore_basket_numRecipes')
  unset(cookie, 'goustoStateStore_basket_date')
  unset(cookie, 'goustoStateStore_menu_selectedRecipeVariants')
}
